import React from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Link } from 'react-router-dom';
import { CONSENT_LABELS } from '../lib/constants';
import useCurrentLocale from '../hooks/useLanguage';
import { ConsentCheckboxesProfileProps, ConsentConfigItem } from '../lib';

type ConsentKey = keyof typeof CONSENT_LABELS;
export type ConsentConfig = Partial<Record<ConsentKey, ConsentConfigItem>>;
type ConsentCheckboxesSignupProps = {
  config: ConsentConfig;
  values: Partial<Record<ConsentKey, boolean>>;
  onChange: (values: Partial<Record<ConsentKey, boolean>>) => void;
  privacyPolicyUrl?: string;
};

export function ConsentCheckboxesSignup({
  config,
  values,
  onChange,
  privacyPolicyUrl,
}: ConsentCheckboxesSignupProps) {
  const lang = useCurrentLocale();
  const keys = (Object.keys(config) || []) as ConsentKey[];
  const handleChange = (key: ConsentKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...values, [key]: e.target.checked });
  };
  return (
    <div className="consent-checkboxes mt-3">
      {keys.map((key) => {
        const item = config[key];
        if (!item) return null;
        const text = (item.message || "").trim();
        const label = text || CONSENT_LABELS[key];
        if (!label || !label.trim()) return null;
        const mandatory = item.mandatory;
        const labelNode = (
          <span className="">
            {label}
            {item.includePrivacyLink && (
              <Link
                to={`/${lang}/privacy-policy`}
                className="ms-1 color-theme text-decoration-none"
              >
                Privacy Policy
              </Link>
            )}
            {mandatory && <span className="color-theme ms-1">*</span>}
          </span>
        );
        return (
          <div key={key} className="mb-2">
            <FormControlLabel
              control={
                <Checkbox
                  className="color-theme"
                  checked={values[key] ?? item.preChecked}
                  onChange={handleChange(key)}
                  color="error"
                />
              }
              label={labelNode}
              className="color-white"
            />
          </div>
        );
      })}
    </div>
  );
}

export function ConsentCheckboxesProfile({
  values,
  onChange,
  config = {},
  privacyPolicyUrl,
}: ConsentCheckboxesProfileProps) {
  const lang = useCurrentLocale();
  const keys = (Object.keys(config) || []) as ConsentKey[];
  const handleChange = (key: ConsentKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.checked ? 1 : 0;
    onChange({ ...values, [key]: v } as Partial<Record<ConsentKey, number>>);
  };
  return (
    <div className="consent-checkboxes mt-3">
      {keys.map((key) => {
        const item = config[key];
        if (!item) return null;
        const raw = values[key];
        const checked = raw === 1 || raw === true;
        const text = (item.message || '').trim();
        const baseLabel = text || CONSENT_LABELS[key];
        if (!baseLabel || !baseLabel.trim()) return null;
        const mandatory = item.mandatory;
        const labelNode = (
          <span className="">
            {baseLabel}
            {item?.includePrivacyLink && (
              <Link
              to={`/${lang}/privacy-policy`}
              className="ms-1 color-theme text-decoration-none"
              >
                Privacy Policy
              </Link>
            )}
            {mandatory && <span className="color-theme ms-1">*</span>}
          </span>
        );
        return (
          <div key={key} className="mb-2 d-flex">
            <FormControlLabel
              control={
                <Checkbox
                className="color-theme"
                  checked={checked}
                  onChange={handleChange(key)}
                  color="error"
                />
              }
              label={labelNode}
              className="color-white"
            />
          </div>
        );
      })}
    </div>
  );
}
